import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Grid, Autocomplete } from "@mui/material";
import SelectField, { Option } from "../../components/ui/select/Select"; // Adjust path as needed

interface LocationValue {
    country: string;
    state: string;
    city: string;
}

interface LocationStepProps {
    value: {
        country: string;
        state: string;
        city: string;
    };
    onChange: (value: { country: string; state: string; city: string }) => void;
}

// Define options (can be expanded or fetched)
const countryOptions: Option[] = [
    { value: "CA", label: "Canada" },
    { value: "US", label: "United States" },
    { value: "UK", label: "United Kingdom" },
];

const statesByCountry: { [key: string]: Option[] } = {
    CA: [
        { value: "AB", label: "Alberta" },
        { value: "BC", label: "British Columbia" },
        { value: "MB", label: "Manitoba" },
        { value: "NB", label: "New Brunswick" },
        { value: "NL", label: "Newfoundland and Labrador" },
        { value: "NS", label: "Nova Scotia" },
        { value: "NT", label: "Northwest Territories" },
        { value: "NU", label: "Nunavut" },
        { value: "ON", label: "Ontario" },
        { value: "PE", label: "Prince Edward Island" },
        { value: "QC", label: "Quebec" },
        { value: "SK", label: "Saskatchewan" },
        { value: "YT", label: "Yukon" },
    ],
    US: [
        { value: "AL", label: "Alabama" },
        { value: "AK", label: "Alaska" },
        { value: "AZ", label: "Arizona" },
        { value: "AR", label: "Arkansas" },
        { value: "CA", label: "California" },
        { value: "CO", label: "Colorado" },
        { value: "CT", label: "Connecticut" },
        { value: "DE", label: "Delaware" },
        { value: "FL", label: "Florida" },
        { value: "GA", label: "Georgia" },
        { value: "HI", label: "Hawaii" },
        { value: "ID", label: "Idaho" },
        { value: "IL", label: "Illinois" },
        { value: "IN", label: "Indiana" },
        { value: "IA", label: "Iowa" },
        { value: "KS", label: "Kansas" },
        { value: "KY", label: "Kentucky" },
        { value: "LA", label: "Louisiana" },
        { value: "ME", label: "Maine" },
        { value: "MD", label: "Maryland" },
        { value: "MA", label: "Massachusetts" },
        { value: "MI", label: "Michigan" },
        { value: "MN", label: "Minnesota" },
        { value: "MS", label: "Mississippi" },
        { value: "MO", label: "Missouri" },
        { value: "MT", label: "Montana" },
        { value: "NE", label: "Nebraska" },
        { value: "NV", label: "Nevada" },
        { value: "NH", label: "New Hampshire" },
        { value: "NJ", label: "New Jersey" },
        { value: "NM", label: "New Mexico" },
        { value: "NY", label: "New York" },
        { value: "NC", label: "North Carolina" },
        { value: "ND", label: "North Dakota" },
        { value: "OH", label: "Ohio" },
        { value: "OK", label: "Oklahoma" },
        { value: "OR", label: "Oregon" },
        { value: "PA", label: "Pennsylvania" },
        { value: "RI", label: "Rhode Island" },
        { value: "SC", label: "South Carolina" },
        { value: "SD", label: "South Dakota" },
        { value: "TN", label: "Tennessee" },
        { value: "TX", label: "Texas" },
        { value: "UT", label: "Utah" },
        { value: "VT", label: "Vermont" },
        { value: "VA", label: "Virginia" },
        { value: "WA", label: "Washington" },
        { value: "WV", label: "West Virginia" },
        { value: "WI", label: "Wisconsin" },
        { value: "WY", label: "Wyoming" },
    ],
    UK: [
        { value: "England", label: "England" },
        { value: "Scotland", label: "Scotland" },
        { value: "Wales", label: "Wales" },
        { value: "Northern Ireland", label: "Northern Ireland" },
    ],
};

const statePlaceholderByCountry: { [key: string]: string } = {
    CA: "Select Province",
    US: "Select State",
    UK: "Select Country",
};

const LocationStep: React.FC<LocationStepProps> = ({ value, onChange }) => {
    const [stateOptions, setStateOptions] = useState<Option[]>([]);
    const [statePlaceholder, setStatePlaceholder] = useState<string>("Select State/Province");

    // Update state/province options when country changes
    useEffect(() => {
        if (value.country) {
            setStateOptions(statesByCountry[value.country] || []);
            setStatePlaceholder(
                statePlaceholderByCountry[value.country] || "Select State/Province",
            );
        } else {
            setStateOptions([]);
            setStatePlaceholder("Select State/Province");
        }
    }, [value.country]);

    const handleCountryChange = (selectedOption: Option | null) => {
        onChange({
            ...value,
            country: selectedOption?.value || "",
            state: "", // Reset state when country changes
        });
    };

    const handleStateChange = (selectedOption: Option | null) => {
        onChange({
            ...value,
            state: selectedOption?.value || "",
        });
    };

    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...value,
            city: event.target.value,
        });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom align="center">
                Where are you located?
            </Typography>
            <Typography
                variant="body2"
                color="textSecondary"
                gutterBottom
                align="center"
                sx={{ mb: 3 }}
            >
                This helps in connecting you with local opportunities and professionals.
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Autocomplete
                        options={countryOptions}
                        getOptionLabel={(option) => option.label}
                        value={
                            countryOptions.find((option) => option.value === value.country) || null
                        }
                        onChange={(event, newValue) => {
                            handleCountryChange(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Country" variant="outlined" />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Autocomplete
                        options={stateOptions}
                        getOptionLabel={(option) => option.label}
                        value={stateOptions.find((option) => option.value === value.state) || null}
                        onChange={(event, newValue) => {
                            handleStateChange(newValue);
                        }}
                        disabled={!value.country}
                        renderInput={(params) => (
                            <TextField {...params} label={statePlaceholder} variant="outlined" />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="City"
                        variant="outlined"
                        fullWidth
                        name="city"
                        value={value.city}
                        onChange={handleCityChange}
                        disabled={!value.state}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default LocationStep;
